import * as React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import TableContainer from '@mui/material/TableContainer';
import ResponsiveTable from '../Utilities/ResponsiveTable';
import SortableTableHead, { HeadCell, Order } from '../Utilities/SortableTableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import { ResponseType, TokenRefreshContext } from '../Contexts/TokenRefreshContext';
import authService from '../api-authorization/AuthorizeService';
import AssetActionsTableRow from './AssetActionsTableRow';
import ActionViewmodel from '../Assets/Viewmodels/ActionViewmodel';
import { debounce } from 'lodash';
import { Autocomplete } from '@mui/material';
import DropdownViewModel from '../Utilities/ViewModels/DropdownViewModel';

interface IProps {
    isClient: boolean;
}

const headCells: HeadCell<ActionViewmodel>[] = [
    { id: 'asset', property: 'Asset', align: "left", disablePadding: false, label: 'Asset' },
    { id: 'type', property: 'Type', align: "left", disablePadding: false, label: 'Action Type' },
    { id: 'description', property: 'Description', align: "left", disablePadding: false, label: 'Description' },
    { id: 'date', property: 'Date', align: "left", disablePadding: false, label: 'Due Date' },
    { id: 'complete', property: 'Complete', align: "left", disablePadding: false, label: 'Status' },
    { id: 'hasComments', property: 'HasComments', align: "left", disablePadding: false, label: 'Comments' },
    { id: 'outcome', property: 'Outcome', align: "left", disablePadding: false, label: 'Outcome' },
];

export default function AssetActionsTable({ isClient }: IProps) {
    const { crabFetch } = React.useContext(TokenRefreshContext);
    const [search, setSearch] = React.useState('');
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [count, setCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [page, setPage] = React.useState(0);
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof ActionViewmodel>('date');
    const [results, setResults] = React.useState<ActionViewmodel[]>([]);
    const [type, setType] = React.useState<string>('All');
    const [status, setStatus] = React.useState<string>('All');
    const [clientList, setClientList] = React.useState<DropdownViewModel[]>([]);
    const [client, setClient] = React.useState<number>(0);
    const [userId, setUserId] = React.useState<string | null>(null);

    React.useEffect(() => {
        authService.getUser().then(user => { setUserId(user.sub); console.log(user) });
       
    }, []);

    React.useEffect(() => {
        getDropdown();
    }, []);

    React.useEffect(() => {
        var index = headCells.findIndex(f => f.id === 'client');

        if (!isClient && index === -1)
            headCells.unshift({ id: 'client', property: 'Client', align: "left", disablePadding: false, label: 'Client' });
        else if (isClient) {
            if (index > -1)
                headCells.splice(index, 1);
        }
    }, [isClient]);

    React.useEffect(() => {
        getData();
    }, [page, rowsPerPage, order, orderBy]);

    React.useEffect(() => {
        searchData();
    }, [type, status, search, client]);

    const getDropdown = async () => {
        const token = await authService.getAccessToken();

        await crabFetch(`Client/GetClientsDropDown`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.JSON,
            (data: DropdownViewModel[]) => {
                const newDropdownItem = new DropdownViewModel();
                newDropdownItem.id = 0;
                newDropdownItem.label = 'All Clients';
                data.unshift(newDropdownItem);
                setClientList(data);
            }
        );
    }

    const getData = async () => {
        setLoading(true);
        const token = await authService.getAccessToken();
        const orderByProp = headCells.find(f => f.id === orderBy)!.property;

        await crabFetch(`Dashboard/GetAssetActionsTable?orderBy=${orderByProp}&order=${order}&page=${page}&pageRows=${rowsPerPage}&type=${type === 'All' ? '' : type}&status=${status === 'All' ? '' : status === 'true' ? true : false}&search=${search}&client=${(client === null || client === 0) ? '' : client}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.JSON,
            (data: any) => {
                setResults(data.rows);
                setCount(data.count);
                setLoading(false);
            },
            () => {
                setLoading(false);
            }
        );
    }

    const onClientChange = (input: string) => {
        const idString = clientList.find(c => c.label === input)?.id;
        const parsedId = parseInt(idString as string ?? '0', 10);

        if (!isNaN(parsedId)) {
            setClient(parsedId);
        }
    }

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof ActionViewmodel) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearch(event.target.value);
    };

    const debouncedhandleSearch = React.useCallback!(debounce(handleSearch, 600), []);

    const emptyRows = page > 0 ? Math.max(0, rowsPerPage - results.length) : 0;

    const searchData = () => {
        if (page === 0) {
            getData();
        }
        setPage(0);
    }

    const keyPress = (event: any) => {
        if (event.keyCode === 13) {
            searchData();
        }
    }
    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Paper>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Grid container alignItems="center" justifyContent="space-between">
                                <Grid item>
                                    <Grid container spacing={1} alignItems="center">
                                        <Grid item xs={12}>
                                            <Typography variant="h2">Asset Actions</Typography>
                                        </Grid>
                                        {(!isClient && clientList.length > 0) &&
                                            <Grid item>
                                                <Autocomplete
                                                    disablePortal
                                                    size="small"
                                                    getOptionKey={(option) => option.id}
                                                    options={clientList}
                                                    sx={{ width: 250 }}
                                                    onInputChange={(value, newInputValue) => onClientChange(newInputValue)}
                                                    value={clientList.find(f => f.id === client)}
                                                    renderInput={(params) => <TextField {...params} />}
                                                />
                                            </Grid>
                                        }
                                        <Grid item>
                                            <TextField
                                                color="primary"
                                                size="small"
                                                variant="outlined"
                                                select
                                                onChange={(value) => setType(value.target.value)}
                                                value={type}
                                            >
                                                <MenuItem value={'All'}>All Action Types</MenuItem>
                                                <MenuItem value={'Mandatory'}>Mandatory</MenuItem>
                                                <MenuItem value={'Recommendation'}>Recommended</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item>
                                            <TextField
                                                color="primary"
                                                size="small"
                                                variant="outlined"
                                                select
                                                onChange={(value) => setStatus(value.target.value)}
                                                value={status}
                                            >
                                                <MenuItem value={'All'}>All Statuses</MenuItem>
                                                <MenuItem value={'false'}>Outstanding</MenuItem>
                                                <MenuItem value={'true'}>Completed</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item>
                                            <TextField
                                                sx={{ minWidth: 300 }}
                                                color="primary"
                                                size="small"
                                                variant="outlined"
                                                onChange={debouncedhandleSearch}
                                                placeholder="Search..."
                                                InputProps={{ endAdornment: <SearchIcon /> }}
                                                onKeyDown={keyPress}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <TableContainer>
                                <ResponsiveTable
                                    aria-labelledby="tableTitle"
                                    size={'medium'}
                                    aria-label="actions table"
                                >
                                    <SortableTableHead
                                        order={order}
                                        orderBy={orderBy}
                                        onRequestSort={handleRequestSort}
                                        headCells={headCells}
                                        lastCells={<TableCell />}
                                    />
                                    <TableBody>
                                        {(!loading && results) && results.map((row: ActionViewmodel) => {
                                            return (<AssetActionsTableRow key={row.id} row={row} refresh={getData} isClient={isClient} currentUserid={userId } />);
                                        })
                                        }
                                        {count <= 0 && !loading &&
                                            <TableRow
                                                sx={{
                                                    height: 53 * rowsPerPage,
                                                }}
                                            >
                                                <TableCell colSpan={headCells.length + 1} align="center">No Results Found</TableCell>
                                            </TableRow>
                                        }
                                        {count > 0 && emptyRows > 0 && (
                                            <TableRow
                                                style={{
                                                    height: 53 * emptyRows,
                                                }}
                                            >
                                                <TableCell colSpan={headCells.length + 1} />
                                            </TableRow>
                                        )}
                                        {loading &&
                                            <TableRow
                                                sx={{
                                                    height: 53 * rowsPerPage,
                                                }}
                                            >
                                                <TableCell colSpan={headCells.length + 1} align="center"><CircularProgress color="primary" /></TableCell>
                                            </TableRow>
                                        }
                                    </TableBody>
                                </ResponsiveTable>
                            </TableContainer>
                            <Grid container justifyContent="space-between" alignItems="center">
                                <Grid item>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25]}
                                        component="div"
                                        count={count}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );
}